import { useState, useEffect } from 'react';
import httpClient from '../httpClient';
import { NotificationTemplate } from '@/models/notification';

interface NotificationTemplateFormData {
    name: string;
    type: string;
    description: string;
    supported_languages: string[];
    channels: string[];
    variables: string[];
    is_active: boolean;
    is_system_template: boolean;
}

export const useNotificationTemplates = () => {
    const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchTemplates = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await httpClient.get('/crm/notification-templates');
            setTemplates(response.data.data || []);
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch templates');
            console.error('Error fetching templates:', err);
        } finally {
            setLoading(false);
        }
    };

    const updateTemplate = async (templateId: string, data: NotificationTemplateFormData) => {
        try {
            setError(null);
            const response = await httpClient.put(
                `/crm/notification-templates/${templateId}`,
                data
            );

            // Update the template in the local state
            setTemplates(prev =>
                prev.map(template =>
                    template.id === templateId ? { ...template, ...response.data.data } : template
                )
            );

            return response.data.data;
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to update template');
            console.error('Error updating template:', err);
            throw err;
        }
    };

    const createTemplate = async (data: NotificationTemplateFormData) => {
        try {
            setError(null);
            const response = await httpClient.post('/crm/notification-templates', data);

            // Add the new template to the local state
            setTemplates(prev => [...prev, response.data.data]);

            return response.data.data;
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to create template');
            console.error('Error creating template:', err);
            throw err;
        }
    };

    const deleteTemplate = async (templateId: string) => {
        try {
            setError(null);
            await httpClient.delete(`/crm/notification-templates/${templateId}`);

            // Remove the template from the local state
            setTemplates(prev => prev.filter(template => template.id !== templateId));
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to delete template');
            console.error('Error deleting template:', err);
            throw err;
        }
    };

    const testTemplate = async (templateId: string) => {
        try {
            setError(null);
            const response = await httpClient.post(
                `/crm/notification-templates/${templateId}/test`
            );
            return response.data;
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to test template');
            console.error('Error testing template:', err);
            throw err;
        }
    };

    const getTemplate = async (templateId: string) => {
        try {
            setError(null);
            const response = await httpClient.get(`/crm/notification-templates/${templateId}`);
            return response.data.data;
        } catch (err: any) {
            setError(err.response?.data?.message || 'Failed to fetch template');
            console.error('Error fetching template:', err);
            throw err;
        }
    };

    useEffect(() => {
        fetchTemplates();
    }, []);

    return {
        templates,
        loading,
        error,
        fetchTemplates,
        updateTemplate,
        createTemplate,
        deleteTemplate,
        testTemplate,
        getTemplate,
    };
};
